import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BadgeDollarSign } from 'lucide-react';
import { API, getAuthHeaders } from '../../utils/adminAuth';

const STATUS_COLORS = {
  pending: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30',
  approved: 'text-blue-400 bg-blue-400/10 border-blue-400/30',
  paid: 'text-[#2E9B50] bg-[#1C7D3F]/10 border-[#1C7D3F]/30',
  rejected: 'text-red-400 bg-red-400/10 border-red-400/30',
};

const NEXT_STATUSES = {
  pending: [
    { value: 'approved', label: 'Approve', tone: 'primary' },
    { value: 'rejected', label: 'Reject', tone: 'danger' },
  ],
  approved: [
    { value: 'paid', label: 'Mark Paid', tone: 'success' },
    { value: 'rejected', label: 'Reject + Refund', tone: 'danger' },
  ],
  paid: [],
  rejected: [
    { value: 'approved', label: 'Re-approve', tone: 'primary' },
  ],
};

export default function WithdrawalsTab() {
  const [data, setData] = useState({ items: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [actionTarget, setActionTarget] = useState(null); // {wd, status}
  const [adminNote, setAdminNote] = useState('');
  const [processing, setProcessing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? '' : `?status=${filter}`;
      const r = await axios.get(`${API}/admin/withdrawals${params}`, { headers: getAuthHeaders() });
      setData(r.data);
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const applyStatus = async () => {
    if (!actionTarget) return;
    setProcessing(true);
    try {
      await axios.patch(`${API}/admin/withdrawals/${actionTarget.wd.id}`,
        { status: actionTarget.status, admin_note: adminNote.trim() },
        { headers: getAuthHeaders() });
      toast.success(`Marked ${actionTarget.status}`);
      setActionTarget(null);
      setAdminNote('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setProcessing(false);
  };

  const summary = data.summary || {};
  const filters = [
    { key: 'all', label: 'All', count: data.items.length },
    { key: 'pending', label: 'Pending', count: summary.pending?.count || 0, total: summary.pending?.total },
    { key: 'approved', label: 'Approved', count: summary.approved?.count || 0, total: summary.approved?.total },
    { key: 'paid', label: 'Paid', count: summary.paid?.count || 0, total: summary.paid?.total },
    { key: 'rejected', label: 'Rejected', count: summary.rejected?.count || 0, total: summary.rejected?.total },
  ];

  return (
    <div data-testid="withdrawals-tab" className="space-y-4">
      <div>
        <p className="text-xs uppercase tracking-[0.22em] text-[#D4AF37] font-sans">Cashout Queue</p>
        <h2 className="text-xl text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Withdrawal Requests</h2>
        <p className="text-white/40 text-xs font-sans mt-1">
          Money locks the moment an ambassador requests. Approve to confirm, mark paid after GCash transfer. Rejecting refunds their wallet.
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap" data-testid="wd-filters">
        {filters.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`wd-filter-${f.key}`}
            className={`px-3 py-1.5 text-xs font-sans border transition-colors ${
              filter === f.key ? 'bg-[#D4AF37] text-black border-[#D4AF37] font-medium' : 'border-white/15 text-white/60 hover:border-white/30 hover:text-white'
            }`}
          >
            {f.label} <span className="opacity-60">({f.count}{f.total ? ` · ₱${Number(f.total).toLocaleString()}` : ''})</span>
          </button>
        ))}
      </div>

      {loading && <p className="text-white/30 text-sm py-8 text-center font-sans bg-[#141414]">Loading…</p>}

      {!loading && data.items.length === 0 && (
        <div className="bg-[#141414] border border-white/5 p-12 text-center">
          <BadgeDollarSign className="w-8 h-8 text-white/15 mx-auto mb-2" />
          <p className="text-white/40 text-sm font-sans">No withdrawals to process.</p>
        </div>
      )}

      {!loading && data.items.length > 0 && (
        <div className="space-y-2" data-testid="wd-list">
          {data.items.map(w => (
            <div key={w.id} className="bg-[#141414] border border-white/5 p-4" data-testid={`wd-row-${w.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-white text-sm font-medium font-sans">{w.ambassador_name || w.ambassador_email}</h3>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${STATUS_COLORS[w.status]}`}>
                      {w.status}
                    </span>
                  </div>
                  <p className="text-white/40 text-xs font-sans">{w.ambassador_email}</p>
                  <div className="mt-2 grid sm:grid-cols-3 gap-x-4 gap-y-1 text-xs font-sans">
                    <div>
                      <span className="text-white/40">Amount: </span>
                      <span className="text-[#D4AF37] font-medium">₱{Number(w.amount).toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-white/40">GCash #: </span>
                      <span className="text-white font-mono">{w.gcash_number}</span>
                    </div>
                    <div>
                      <span className="text-white/40">GCash Name: </span>
                      <span className="text-white">{w.gcash_name}</span>
                    </div>
                  </div>
                  {w.note && <p className="text-white/50 text-xs italic mt-2 font-sans">Note: {w.note}</p>}
                  {w.admin_note && (
                    <p className="text-white/40 text-xs mt-1 font-sans">
                      <span className="text-[#D4AF37]">Admin:</span> {w.admin_note}
                    </p>
                  )}
                  <p className="text-white/30 text-[10px] font-sans mt-2">
                    Requested {new Date(w.created_at).toLocaleString('en-PH')}
                    {w.processed_by && <> · processed by {w.processed_by}</>}
                  </p>
                </div>
                <div className="flex flex-col gap-1.5 flex-shrink-0">
                  {(NEXT_STATUSES[w.status] || []).map(next => (
                    <button
                      key={next.value}
                      onClick={() => { setActionTarget({ wd: w, status: next.value }); setAdminNote(''); }}
                      data-testid={`wd-action-${w.id}-${next.value}`}
                      className={`text-xs px-3 py-1.5 font-sans uppercase tracking-wider transition-colors border ${
                        next.tone === 'success' ? 'bg-[#1C7D3F]/10 text-[#2E9B50] border-[#1C7D3F]/40 hover:bg-[#1C7D3F]/20' :
                        next.tone === 'danger' ? 'bg-red-400/10 text-red-400 border-red-400/40 hover:bg-red-400/20' :
                        'bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/40 hover:bg-[#D4AF37]/20'
                      }`}
                    >
                      {next.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Action confirm modal */}
      {actionTarget && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="wd-action-modal">
          <div className="bg-[#141414] border border-white/10 w-full max-w-md p-5">
            <h3 className="text-white text-base font-sans font-medium mb-2">
              {actionTarget.status === 'approved' && '✓ Approve withdrawal'}
              {actionTarget.status === 'paid' && '💰 Mark as paid'}
              {actionTarget.status === 'rejected' && '✗ Reject (refunds wallet)'}
            </h3>
            <p className="text-white/60 text-xs font-sans mb-3">
              <strong className="text-[#D4AF37]">₱{Number(actionTarget.wd.amount).toLocaleString()}</strong>
              {' '} to {actionTarget.wd.gcash_name} ({actionTarget.wd.gcash_number})
            </p>
            <textarea
              value={adminNote}
              onChange={e => setAdminNote(e.target.value)}
              placeholder={
                actionTarget.status === 'paid' ? 'Optional: reference number from GCash' :
                actionTarget.status === 'rejected' ? 'Required: reason for rejection' :
                'Optional note for the ambassador'
              }
              rows={2}
              className="w-full bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm text-white focus:border-[#D4AF37] focus:outline-none resize-none"
              data-testid="wd-admin-note"
            />
            <div className="flex gap-2 justify-end mt-3">
              <button onClick={() => setActionTarget(null)}
                className="px-4 py-2 text-xs uppercase tracking-[0.18em] border border-white/15 text-white/60 hover:text-white">
                Cancel
              </button>
              <button
                onClick={applyStatus}
                disabled={processing || (actionTarget.status === 'rejected' && !adminNote.trim())}
                data-testid="wd-confirm-btn"
                className="px-4 py-2 text-xs uppercase tracking-[0.18em] bg-[#D4AF37] text-black font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#E5C158] transition-colors"
              >
                {processing ? '…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
